import { useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';

const ImportResultsModal = ({ show, onHide, results }) => {
  if (!show || !results) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Import Results</h5>
            <button type="button" className="btn-close" onClick={onHide}></button>
          </div>
          <div className="modal-body">
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card bg-primary text-white">
                  <div className="card-body text-center">
                    <h3>{results.total_rows}</h3>
                    <small>Total Rows</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-success text-white">
                  <div className="card-body text-center">
                    <h3>{results.categories_created || 0}</h3>
                    <small>Categories</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-info text-white">
                  <div className="card-body text-center">
                    <h3>{results.items_created || results.items_updated || 0}</h3>
                    <small>{results.items_updated ? 'Items Updated' : 'Items Created'}</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-warning text-white">
                  <div className="card-body text-center">
                    <h3>{results.lots_created || results.adjustments_made || 0}</h3>
                    <small>{results.adjustments_made ? 'Adjustments' : 'Lots Created'}</small>
                  </div>
                </div>
              </div>
            </div>

            {results.errors && results.errors.length > 0 && (
              <div className="alert alert-danger">
                <h6>Errors ({results.errors.length}):</h6>
                <ul className="mb-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {results.errors.map((error, idx) => (
                    <li key={idx}><small>{error}</small></li>
                  ))}
                </ul>
              </div>
            )}

            {results.warnings && results.warnings.length > 0 && (
              <div className="alert alert-warning">
                <h6>Warnings ({results.warnings.length}):</h6>
                <ul className="mb-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {results.warnings.map((warning, idx) => (
                    <li key={idx}><small>{warning}</small></li>
                  ))}
                </ul>
              </div>
            )}

            {(!results.errors || results.errors.length === 0) && (
              <div className="alert alert-success">
                <i className="bi bi-check-circle me-2"></i>
                Import completed successfully{results.warnings?.length > 0 ? ' with warnings' : ' with no errors'}!
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-primary" onClick={onHide}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

ImportResultsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  results: PropTypes.shape({
    total_rows: PropTypes.number,
    categories_created: PropTypes.number,
    items_created: PropTypes.number,
    items_updated: PropTypes.number,
    lots_created: PropTypes.number,
    adjustments_made: PropTypes.number,
    errors: PropTypes.arrayOf(PropTypes.string),
    warnings: PropTypes.arrayOf(PropTypes.string)
  })
};

const ImportPage = () => {
  const [activeTab, setActiveTab] = useState('items');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const endpoints = {
        items: '/import/items',
        inventory: '/import/inventory',
        reconciliation: '/import/reconciliation'
      };

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}${endpoints[activeTab]}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setResults(response.data);
      setShowResults(true);
      setFile(null);
      // Reset file input
      document.getElementById('fileInput').value = '';
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const getTabContent = () => {
    switch (activeTab) {
      case 'items':
        return {
          title: 'Import Items',
          description: 'Create new items with default pricing',
          columns: [
            { name: 'Category', desc: 'Category name (will be created if it doesn\'t exist)' },
            { name: 'Item', desc: 'Item name' },
            { name: 'Default Price', desc: 'Default unit price (e.g., $25.00)' },
            { name: 'Unit of Measure', desc: 'Optional: each, box, pound, etc. (defaults to "each")' }
          ],
          steps: [
            'Categories are created if they don\'t exist',
            'Items are created with the specified default price',
            'Items can be edited later to update details',
            'No inventory lots are created (use Inventory import for that)'
          ]
        };
      case 'inventory':
        return {
          title: 'Import Inventory',
          description: 'Add inventory lots for existing or new items',
          columns: [
            { name: 'Category', desc: 'Category name' },
            { name: 'Item', desc: 'Item name (will be created if it doesn\'t exist)' },
            { name: 'Quantity', desc: 'Quantity to add' },
            { name: 'Price', desc: 'Optional: Unit price (uses item\'s default price if blank)' },
            { name: 'Date', desc: 'Optional: Purchase date (YYYY-MM-DD format, defaults to today)' }
          ],
          steps: [
            'Items are created if they don\'t exist',
            'If price is blank, uses the item\'s default price',
            'Creates inventory lots with specified quantity',
            'Date defaults to today if not provided'
          ]
        };
      case 'reconciliation':
        return {
          title: 'Import Reconciliation',
          description: 'Adjust inventory based on physical count',
          columns: [
            { name: 'Category', desc: 'Category name' },
            { name: 'Item', desc: 'Item name (must exist)' },
            { name: 'Physical Count', desc: 'Actual quantity counted' },
            { name: 'Notes', desc: 'Optional: Reason for adjustment' }
          ],
          steps: [
            'Compares physical count to system quantity',
            'Creates adjustment transactions (credit or debit)',
            'Adjustments use the item\'s average cost',
            'All adjustments are logged in transaction ledger',
            'Items must already exist in the system'
          ]
        };
      default:
        return null;
    }
  };

  const tabContent = getTabContent();

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Import Data</h2>
      </div>

      <div className="row">
        <div className="col-md-10 offset-md-1">
          {/* Tabs */}
          <ul className="nav nav-tabs mb-4">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'items' ? 'active' : ''}`}
                onClick={() => { setActiveTab('items'); setFile(null); setError(''); }}
              >
                <i className="bi bi-box-seam me-2"></i>
                Import Items
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'inventory' ? 'active' : ''}`}
                onClick={() => { setActiveTab('inventory'); setFile(null); setError(''); }}
              >
                <i className="bi bi-archive me-2"></i>
                Import Inventory
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'reconciliation' ? 'active' : ''}`}
                onClick={() => { setActiveTab('reconciliation'); setFile(null); setError(''); }}
              >
                <i className="bi bi-clipboard-check me-2"></i>
                Reconciliation
              </button>
            </li>
          </ul>

          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-4">{tabContent.title}</h5>
              <p className="text-muted mb-4">{tabContent.description}</p>

              <div className="alert alert-info">
                <h6><i className="bi bi-info-circle me-2"></i>CSV Format</h6>
                <p className="mb-2">Your CSV file should have the following columns:</p>
                <ul className="mb-0">
                  {tabContent.columns.map((col, idx) => (
                    <li key={idx}>
                      <strong>{col.name}</strong> - {col.desc}
                    </li>
                  ))}
                </ul>
              </div>

              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {error}
                  <button type="button" className="btn-close" onClick={() => setError('')}></button>
                </div>
              )}

              <div className="mb-4">
                <label htmlFor="fileInput" className="form-label">Select CSV File</label>
                <input
                  id="fileInput"
                  type="file"
                  className="form-control"
                  accept=".csv"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
                {file && (
                  <div className="mt-2 text-success">
                    <i className="bi bi-file-earmark-check me-2"></i>
                    Selected: {file.name}
                  </div>
                )}
              </div>

              <div className="d-grid">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={handleUpload}
                  disabled={!file || uploading}
                >
                  {uploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-upload me-2"></i>
                      Upload and Import
                    </>
                  )}
                </button>
              </div>

              <div className="mt-4">
                <h6>What happens during import:</h6>
                <ol>
                  {tabContent.steps.map((step, idx) => (
                    <li key={idx}>{step}</li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ImportResultsModal
        show={showResults}
        onHide={() => setShowResults(false)}
        results={results}
      />
    </div>
  );
};

export default ImportPage;
